import { supabase } from '@/lib/supabase'

/**
 * Custom Error class for API errors
 */
export class ApiError extends Error {
  status: number
  detail?: string | any

  constructor(message: string, status: number, detail?: string | any) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

/**
 * Base API client with authentication headers
 */
export class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl
  }

  getBaseUrl(): string {
    return this.baseUrl
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (supabase) {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`
        console.log('🔐 API Client: Using session token for authentication')
      } else {
        console.log('⚠️ API Client: No session token available')
      }
    } else {
      console.log('⚠️ API Client: Supabase client not available')
    }

    return headers
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const headers = await this.getAuthHeaders()

    console.log(`🌐 API Client: Making request to ${url}`)
    console.log(`🔑 API Client: Headers:`, headers)

    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...headers,
        ...options.headers,
      },
    })

    console.log(`📡 API Client: Response status: ${response.status}`)

    if (response.status === 401) {
      console.log('🚫 API Client: Unauthorized, redirecting to login')
      // Redirect to login if unauthorized
      window.location.href = '/login'
      throw new Error('Unauthorized')
    }

    if (!response.ok) {
      console.log(`❌ API Client: Request failed with status ${response.status}`)
      // Try to parse error details from response body
      try {
        const errorData = await response.json()
        const errorMessage = errorData.detail || errorData.message || `HTTP error! status: ${response.status}`
        throw new ApiError(errorMessage, response.status, errorData.detail)
      } catch (parseError) {
        if (parseError instanceof ApiError) throw parseError
        // If parsing fails, throw generic error
        throw new ApiError(`HTTP error! status: ${response.status}`, response.status)
      }
    }

    return response.json()
  }

  async get<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' })
  }

  async post<T>(endpoint: string, data?: any, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async put<T>(endpoint: string, data?: any, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async patch<T>(endpoint: string, data?: any, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async delete<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' })
  }
}

// Default API client for the backend
export const apiClient = new ApiClient(import.meta.env.VITE_API_URL || 'http://localhost:8000')
